import { animate, style, transition, trigger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import { NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ContentService } from 'src/app/services/content/content.service';
import { CenterSectionComponent, TextButtonComponent, TextInputComponent } from 'src/app/shared/components';
import { TypedFormGroup, buildFormGroup } from 'src/app/shared/forms';
import { environment } from 'src/environments/environment';
import { ContentSection } from '../../../shared/layout/content-section';
import { SanitizeHtmlPipe } from '../../../shared/pipes/sanitize-html.pipe';

interface Contact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
  subject: string;
}

interface ContactPost {
  firstName: string;
  lastName: string;
  fromEmail: string;
  phone: string;
  message: string;
  subject: string;
}

type SnackbarType = 'primary' | 'warn';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss'],
  animations: [
    trigger('mapAnimation', [
      transition(':enter', [style({ opacity: 0.5, height: 0 }), animate('300ms ease-out', style({ opacity: 1, height: '*' }))]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CenterSectionComponent, SanitizeHtmlPipe, ReactiveFormsModule, MatRadioModule, TextInputComponent, TextButtonComponent],
})
export class ContactUsComponent extends ContentSection implements OnInit {
  private sanitizer = inject(DomSanitizer);
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);

  apiContactUrl = `${environment.apiOrigin}/Email/ContactUs`;
  googleApiUrl: SafeResourceUrl;

  @ViewChild('formDirective') private formDirective: NgForm;

  title: string;
  description: string;

  formContent: {
    firstNameLabel: string;
    firstNamePlaceholder: string;
    lastNameLabel: string;
    lastNamePlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    phoneLabel: string;
    phonePlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    subjects: string[];
  };
  submitButton: string;

  messageDuration: number;
  messageSuccess: string;
  messageError: string;
  messageCloseButton: string;

  form: TypedFormGroup<Contact>;
  hideContactForm: boolean;

  constructor() {
    const contentService = inject(ContentService);
    super(contentService, 'contact', { meta: true });

    this.apiContactUrl = this.apiContactUrl += `/${this.contentService.getMerchantId()}`;

    const key = this.content.get('googleAPIKey');
    const address = this.content.get('storeAddressText');

    if (key && address) {
      this.googleApiUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.google.com/maps/embed/v1/place?q=${address}&key=${key}`,
      );
    }

    this.title = this.content.get('titleText');
    this.description = this.contentService.addImageSource(this.content.get('descriptionText'));
    this.hideContactForm = this.content.get('hideContactForm');

    this.formContent = {
      firstNameLabel: this.content.get('firstNameLabelText') || 'Fornavn',
      firstNamePlaceholder: this.content.get('firstNamePlaceholderText') || 'Indtast fornavn',
      lastNameLabel: this.content.get('lastNameLabelText') || 'Efternavn',
      lastNamePlaceholder: this.content.get('lastNamePlaceholderText') || 'Indtast efternavn',
      emailLabel: this.content.get('emailLabelText') || 'E-mail',
      emailPlaceholder: this.content.get('emailPlaceholderText') || 'Indtast e-mail',
      phoneLabel: this.content.get('phoneLabelText') || 'Telefon',
      phonePlaceholder: this.content.get('phonePlaceholderText') || 'Indtast telefon',
      messageLabel: this.content.get('messageLabelText') || 'Besked',
      messagePlaceholder: this.content.get('messagePlaceholderText') || 'Hvad har du på hjerte?',
      subjects: this.content.get('subjects'),
    };
    this.submitButton = this.content.get('sendMessageButtonText');

    this.messageDuration = 3000;
    this.messageSuccess = this.content.get('successMessageText');
    this.messageError = this.content.get('errorMessageText');
    this.messageCloseButton = this.content.get('closeButtonText');
  }

  ngOnInit() {
    this.form = this.createForm({
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      phone: undefined,
      message: undefined,
      subject: undefined,
    });
  }

  postContactForm() {
    if (this.form.invalid) {
      return;
    }

    const contactForm = {
      firstName: this.form.value.firstName,
      lastName: this.form.value.lastName,
      fromEmail: this.form.value.email,
      phone: this.form.value.phone,
      message: this.form.value.message,
      subject: this.form.value.subject,
    };

    this.http.post<ContactPost>(this.apiContactUrl, contactForm, { observe: 'response' }).subscribe({
      next: () => {
        this.openSnackBar(this.messageSuccess);
        this.formDirective.resetForm();
      },
      error: () => this.openSnackBar(this.messageError, 'warn'),
    });
  }

  private openSnackBar(message: string, type: SnackbarType = 'primary') {
    this.snackBar.open(message, this.messageCloseButton, {
      duration: this.messageDuration,
      panelClass: type,
    });
  }

  private createForm(customer: Contact): TypedFormGroup<Contact> {
    const form = buildFormGroup(customer, { updateOn: 'submit' });

    form.controls.firstName.setValidators([Validators.required]);
    form.controls.lastName.setValidators([Validators.required]);
    form.controls.email.setValidators([Validators.required, Validators.email]);
    form.controls.phone.setValidators([Validators.required]);
    form.controls.message.setValidators([Validators.required]);

    if (this.formContent.subjects?.length) {
      form.controls.subject.setValidators([Validators.required]);
    }

    return form;
  }
}
